import { Home, ResidentItem } from '@lmc/ts-common'
import { Avatar, Heading } from '@lmc/ui-kit'
import { NAMESPACE } from 'locale'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { getNeedsNames, getResidentInfoDetail } from '../utils/utils'
import { Text } from '@lmc/ui-kit/src/general'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import Description from 'components/generic/LogTimelineRow/Description'
import { GeneralStatusModal } from './GeneralStatusModal'
import { ProfileBadges } from './ProfileBadges'
import { GeneralZoneSection } from './GeneralZoneSection'
import { GeneralNeedsSection } from './GeneralNeedsSection'
import { GeneralDNACPRSection } from './GeneralDNACPRSection'
import { EditTooltip } from './EditTooltip'
import { useParams } from 'react-router-dom'
import { useQuery } from 'react-query'
import requests from 'api'

type GeneralInformationProps = {
  resident: ResidentItem
  home: Home
}

export const GeneralInformation = ({
  resident,
  home,
}: GeneralInformationProps) => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)
  const { id } = useParams()

  const hasPro = home?.features?.pro?.enabled

  const hasSkillsAndNeeds = home?.features?.skillsAndNeeds?.enabled

  const { data: residentNeedsData } = useQuery(
    ['needs', id] as string[],
    ({ queryKey }) =>
      requests.residents.needsList({ params: { id: queryKey[1] } }),
    {
      enabled: hasPro && Boolean(id),
    }
  )

  const hasNeeds = residentNeedsData && residentNeedsData?.result?.length > 0

  const { data: needsData } = useQuery(
    ['needsList'],
    () => requests.needs.list({ params: undefined }),
    {
      enabled: hasPro && hasSkillsAndNeeds && hasNeeds,
    }
  )

  const residentNeeds = getNeedsNames(
    residentNeedsData?.result,
    needsData?.result
  )

  const hasSummarySection = !!resident?.summary

  return (
    <MainWrapper>
      <GeneralWrapper>
        <LeftWrapper>
          <AvatarWrapper>
            <Avatar url={resident?.picture as unknown as string} size={140} />
          </AvatarWrapper>
          <InfoWrapper>
            <Heading level={2}>
              {resident?.preferredName || resident?.name?.first}
            </Heading>
            <StyledText>{getResidentInfoDetail(resident)}</StyledText>
            <ProfileBadges resident={resident} />
          </InfoWrapper>
        </LeftWrapper>
        <GeneralStatusModal resident={resident} />
      </GeneralWrapper>
      {hasPro && <GeneralDNACPRSection resident={resident} />}
      <GeneralZoneSection resident={resident} hasPro={hasPro} />
      {hasPro && hasNeeds && (
        <GeneralNeedsSection needsText={residentNeeds?.join(', ')} />
      )}
      {hasSummarySection && (
        <CoreSectionWrapper>
          <CoreSection>
            <StyledCoreLabel level={2}>
              <EditTooltip title={t(supConstants.PERSONAL_NOTES_TOOLTIP)}>
                {t(supConstants.PERSONAL_NOTES)}
              </EditTooltip>
            </StyledCoreLabel>
            <Description description={resident?.summary} />
          </CoreSection>
        </CoreSectionWrapper>
      )}
    </MainWrapper>
  )
}

const GeneralWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
  }
`

const LeftWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
`

const StyledText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    text-align: center;
    margin-top: 5px;
  }
`

const StyledCoreLabel = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
`

const InfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
`

const CoreSectionWrapper = styled.div`
  border-top: 1px solid;
  border-color: ${(props) => props.theme.color.neutral.color7};
  margin-top: 16px;
  padding-top: 16px;
  display: flex;
  justify-content: space-between;
`

const CoreSection = styled.div`
  display: flex;
  flex-direction: column;
`

const AvatarWrapper = styled.div`
  width: 140px;
  height: 140px;
`

const MainWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${(props) => props.theme.color.neutral.color11};
  padding: 24px;
  border-radius: ${(props) => props.theme.properties.borderRadius.large};
`
